/**
 * DenDispatcher — single source of truth for which Den renders for a
 * given viewer + subject pair.
 *
 * Mounted at three routes (see App.tsx):
 *   /dashboard/dens/:persona  → forced render of a specific Den (preview/test)
 *   /dashboard                → mode="self"  (Den from viewer's self_identity)
 *   /clients/:id              → mode="subject" (Den from subject's track,
 *                                with recruiter overlays when viewer is a
 *                                recruiter looking at a person dossier)
 *
 * NOTE on rollout: this component is ADDITIVE. It mounts only at the new
 * /dashboard/dens/:persona route initially so existing dashboard / client
 * pages continue working unchanged. Wiring the auto-dispatch into
 * /dashboard or /clients/:id is a follow-up swap once each Den has shipped
 * and proven itself in preview mode.
 */
import React, { lazy, Suspense, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { DenLoadingSkeleton } from "./DenLoadingSkeleton";
import { SubjectDenWithRecruiterOverlays } from "./SubjectDenWithRecruiterOverlays";
import { DenSwitchBar } from "@/components/den-switcher/DenSwitchBar";
import { DenSalesArsenalSection } from "./_shared/DenSalesArsenalSection";

// NAV Polish v2 — personas where Sales Arsenal is most relevant. Founder,
// Recruiter, Sales, Agency (=founder for now), Lawyer, Investor.
const SALES_ARSENAL_PERSONAS = new Set([
  "founder", "recruiter", "sales_pro", "lawyer", "investor",
  "consultant", "fractional_executive", "executive",
]);

// Lazy-loaded Den components to keep the initial bundle small.
const AthleteDen = lazy(() =>
  import("./athlete/AthleteDen").then((m) => ({ default: m.AthleteDen }))
);
const RecruiterDen = lazy(() =>
  import("./recruiter/RecruiterDen").then((m) => ({ default: m.RecruiterDen }))
);
const PhysicianDen = lazy(() =>
  import("./physician/PhysicianDen").then((m) => ({ default: m.PhysicianDen }))
);
const LawyerDen = lazy(() =>
  import("./lawyer/LawyerDen").then((m) => ({ default: m.LawyerDen }))
);
const InvestorDen = lazy(() =>
  import("./investor/InvestorDen").then((m) => ({ default: m.InvestorDen }))
);
const AuthorDen = lazy(() =>
  import("./author/AuthorDen").then((m) => ({ default: m.AuthorDen }))
);
const PoliticianDen = lazy(() =>
  import("./politician/PoliticianDen").then((m) => ({ default: m.PoliticianDen }))
);
const ActivistDen = lazy(() =>
  import("./activist/ActivistDen").then((m) => ({ default: m.ActivistDen }))
);
const ReligiousDen = lazy(() =>
  import("./religious/ReligiousDen").then((m) => ({ default: m.ReligiousDen }))
);
const RealEstateAgentDen = lazy(() =>
  import("./real-estate-agent/RealEstateAgentDen").then((m) => ({ default: m.RealEstateAgentDen }))
);
const RealEstateInvestorDen = lazy(() =>
  import("./RealEstateInvestorDen").then((m) => ({ default: m.RealEstateInvestorDen }))
);
const SalesProDen = lazy(() =>
  import("./sales-pro/SalesProDen").then((m) => ({ default: m.SalesProDen }))
);
const CoachDen = lazy(() =>
  import("./coach/CoachDen").then((m) => ({ default: m.CoachDen }))
);
const SpeakerDen = lazy(() =>
  import("./speaker/SpeakerDen").then((m) => ({ default: m.SpeakerDen }))
);
const FractionalDen = lazy(() =>
  import("./fractional/FractionalDen").then((m) => ({ default: m.FractionalDen }))
);
const FinancialAdvisorDen = lazy(() =>
  import("./financial-advisor/FinancialAdvisorDen").then((m) => ({ default: m.FinancialAdvisorDen }))
);
const ConsultantDen = lazy(() =>
  import("./consultant/ConsultantDen").then((m) => ({ default: m.ConsultantDen }))
);
const PodcastHostDen = lazy(() =>
  import("./podcast-host/PodcastHostDen").then((m) => ({ default: m.PodcastHostDen }))
);
const CourseCreatorDen = lazy(() =>
  import("./CourseCreatorDen").then((m) => ({ default: m.CourseCreatorDen }))
);
const CreativeFreelancerDen = lazy(() =>
  import("./CreativeFreelancerDen").then((m) => ({ default: m.CreativeFreelancerDen }))
);
const TherapistDen = lazy(() =>
  import("./TherapistDen").then((m) => ({ default: m.TherapistDen }))
);
const ExecutiveDen = lazy(() =>
  import("./ExecutiveDen").then((m) => ({ default: m.ExecutiveDen }))
);
const JournalistDen = lazy(() =>
  import("./JournalistDen").then((m) => ({ default: m.JournalistDen }))
);
const AcademicDen = lazy(() =>
  import("./AcademicDen").then((m) => ({ default: m.AcademicDen }))
);
const ArtistDen = lazy(() =>
  import("./ArtistDen").then((m) => ({ default: m.ArtistDen }))
);
const MilitaryDen = lazy(() =>
  import("./MilitaryDen").then((m) => ({ default: m.MilitaryDen }))
);

// Founder Den is the legacy MyDen page — kept as the fallback so any
// unknown self_identity / track lands on something familiar.
const FounderDenFallback = lazy(() =>
  import("@/pages/MyDen").then((m) => ({ default: m.default }))
);

interface DenDispatcherProps {
  /**
   * "self"      → render the viewer's own Den based on profiles.self_identity
   * "subject"   → render a Den for the client/:id being viewed,
   *               based on clients.track (with recruiter overlays applied
   *               when viewer.self_identity === 'recruiter')
   * forcePersona overrides both modes (preview/test).
   */
  mode: "self" | "subject";
  forcePersona?: string;
}

/**
 * Map of viewer.self_identity → Den slug. Saul's lock: 'solopreneur' rolls
 * up to 'founder' (one identity, same Den). 'small_co_ceo' too. Future
 * identities (agency_owner, marketer, investor, etc.) fall through to
 * the founder fallback until those Dens ship.
 */
const SELF_IDENTITY_TO_DEN: Record<string, string> = {
  founder: "founder",
  solopreneur: "founder",
  small_co_ceo: "founder",
  recruiter: "recruiter",
  athlete: "athlete",
  physician: "physician",
  doctor: "physician",
  lawyer: "lawyer",
  attorney: "lawyer",
  investor: "investor",
  vc: "investor",
  author: "author",
  writer: "author",
  politician: "politician",
  activist: "activist",
  religious: "religious",
  clergy: "religious",
  real_estate_agent: "real_estate_agent",
  realtor: "real_estate_agent",
  sales_pro: "sales_pro",
  account_executive: "sales_pro",
  ae: "sales_pro",
  coach: "coach",
  executive_coach: "coach",
  speaker: "speaker",
  keynote_speaker: "speaker",
  fractional_executive: "fractional_executive",
  fractional: "fractional_executive",
  fractional_cfo: "fractional_executive",
  fractional_cmo: "fractional_executive",
  fractional_cto: "fractional_executive",
  financial_advisor: "financial_advisor",
  ria: "financial_advisor",
  wealth_manager: "financial_advisor",
  consultant: "consultant",
  independent_consultant: "consultant",
  podcast_host: "podcast_host",
  podcaster: "podcast_host",
  course_creator: "course_creator",
  online_educator: "course_creator",
  creative_freelancer: "creative_freelancer",
  designer: "creative_freelancer",
  freelance_designer: "creative_freelancer",
  freelance_writer: "creative_freelancer",
  videographer: "creative_freelancer",
  photographer: "creative_freelancer",
  illustrator: "creative_freelancer",
  freelance_architect: "creative_freelancer",
  real_estate_investor: "real_estate_investor",
  property_investor: "real_estate_investor",
  therapist: "therapist",
  lcsw: "therapist",
  lmft: "therapist",
  psychologist: "therapist",
  executive: "executive",
  ceo: "executive",
  cfo: "executive",
  cto: "executive",
  cmo: "executive",
  coo: "executive",
  c_suite: "executive",
  journalist: "journalist",
  reporter: "journalist",
  beat_specialist: "journalist",
  academic: "academic",
  professor: "academic",
  postdoc: "academic",
  tenure_track: "academic",
  artist: "artist",
  visual_artist: "artist",
  musician: "artist",
  illustrator_artist: "artist",
  military: "military",
  veteran: "military",
  active_duty: "military",
  transitioning_officer: "military",
  // Reserved for future builds — fall through to founder until shipped.
  agency_owner: "founder",
  marketer: "founder",
};

/**
 * Map of clients.track → Den slug for subject-mode rendering.
 * Includes the 14 tracks the dossier renderer already supports; only
 * tracks with their own Den built render that Den, the rest fall through
 * to the founder Den as a generic person view.
 */
const TRACK_TO_DEN: Record<string, string> = {
  founder: "founder",
  athlete: "athlete",
  physician: "physician",
  doctor: "physician",
  lawyer: "lawyer",
  attorney: "lawyer",
  investor: "investor",
  vc: "investor",
  author: "author",
  writer: "author",
  politician: "politician",
  activist: "activist",
  religious: "religious",
  clergy: "religious",
  real_estate_agent: "real_estate_agent",
  realtor: "real_estate_agent",
  sales_pro: "sales_pro",
  account_executive: "sales_pro",
  ae: "sales_pro",
  coach: "coach",
  executive_coach: "coach",
  speaker: "speaker",
  keynote_speaker: "speaker",
  fractional_executive: "fractional_executive",
  fractional: "fractional_executive",
  fractional_cfo: "fractional_executive",
  fractional_cmo: "fractional_executive",
  fractional_cto: "fractional_executive",
  financial_advisor: "financial_advisor",
  ria: "financial_advisor",
  wealth_manager: "financial_advisor",
  consultant: "consultant",
  independent_consultant: "consultant",
  podcast_host: "podcast_host",
  podcaster: "podcast_host",
  course_creator: "course_creator",
  online_educator: "course_creator",
  creative_freelancer: "creative_freelancer",
  designer: "creative_freelancer",
  freelance_designer: "creative_freelancer",
  freelance_writer: "creative_freelancer",
  videographer: "creative_freelancer",
  photographer: "creative_freelancer",
  illustrator: "creative_freelancer",
  freelance_architect: "creative_freelancer",
  real_estate_investor: "real_estate_investor",
  property_investor: "real_estate_investor",
  therapist: "therapist",
  lcsw: "therapist",
  lmft: "therapist",
  psychologist: "therapist",
  executive: "executive",
  journalist: "journalist",
  reporter: "journalist",
  academic: "academic",
  professor: "academic",
  postdoc: "academic",
  artist: "artist",
  visual_artist: "artist",
  musician: "artist",
  illustrator_artist: "artist",
  military: "military",
  veteran: "military",
  active_duty: "military",
  recruiter: "recruiter",
};

interface ResolvedDen {
  type: string;
  /** When viewer is a recruiter looking at a non-recruiter subject, the
   *  base Den is the subject's track Den, with recruiter overlays added. */
  recruiterOverlays: boolean;
}

export function DenDispatcher({ mode, forcePersona }: DenDispatcherProps) {
  const { id: clientId, persona: routePersona } = useParams();
  const [resolved, setResolved] = useState<ResolvedDen | null>(null);
  const [brand, setBrand] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // 1) Forced persona (preview/test) wins everything.
        const force = forcePersona || routePersona;
        if (force) {
          if (!alive) return;
          setResolved({ type: force, recruiterOverlays: false });
          setLoading(false);
          return;
        }

        // 2) Self mode — read viewer's self_identity from profiles.
        if (mode === "self") {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) {
            if (!alive) return;
            setResolved({ type: "founder", recruiterOverlays: false });
            setLoading(false);
            return;
          }
          const { data: profile } = await supabase
            .from("profiles")
            .select("self_identity")
            .eq("id", user.id)
            .maybeSingle();
          const den =
            SELF_IDENTITY_TO_DEN[(profile as any)?.self_identity || "founder"] ||
            "founder";
          if (!alive) return;
          setResolved({ type: den, recruiterOverlays: false });
          setLoading(false);
          return;
        }

        // 3) Subject mode — read the subject's track AND viewer's identity
        //    so we can decide whether to add recruiter overlays.
        if (mode === "subject" && clientId) {
          const [{ data: { user } }, { data: client }] = await Promise.all([
            supabase.auth.getUser(),
            supabase
              .from("clients")
              .select("*")
              .eq("id", clientId)
              .maybeSingle(),
          ]);

          if (!client) {
            if (!alive) return;
            setErr("Subject not found");
            setLoading(false);
            return;
          }

          let viewerSelfId = "founder";
          if (user?.id) {
            const { data: vp } = await supabase
              .from("profiles")
              .select("self_identity")
              .eq("id", user.id)
              .maybeSingle();
            viewerSelfId = (vp as any)?.self_identity || "founder";
          }

          const baseDen =
            TRACK_TO_DEN[(client as any)?.track || "founder"] || "founder";

          if (!alive) return;
          setBrand(client);
          setResolved({
            type: baseDen,
            // Recruiter overlays apply only when the viewer is a recruiter
            // and the subject is a person (not a brand/company). Recruiter
            // viewing recruiter just renders the recruiter Den directly.
            recruiterOverlays:
              viewerSelfId === "recruiter" &&
              (client as any)?.client_type === "person" &&
              baseDen !== "recruiter",
          });
          setLoading(false);
          return;
        }

        // 4) Fallback — no clientId in subject mode etc.
        if (!alive) return;
        setResolved({ type: "founder", recruiterOverlays: false });
        setLoading(false);
      } catch (e: any) {
        if (!alive) return;
        setErr(String(e?.message || e));
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [mode, clientId, forcePersona, routePersona]);

  if (loading) return <DenLoadingSkeleton />;
  if (err) {
    return (
      <div className="p-6 text-sm text-destructive">
        Den failed to load: {err}
      </div>
    );
  }

  const denType = resolved?.type ?? "founder";

  // Recruiter overlays branch — wrap the subject's Den with overlay
  // panels (mutual connections, reality check, add-to-pipeline CTA).
  if (resolved?.recruiterOverlays) {
    return (
      <div className="space-y-3">
        <DenSwitchBar currentPersona={denType} />
        <Suspense fallback={<DenLoadingSkeleton />}>
          <SubjectDenWithRecruiterOverlays
            baseDenType={denType}
            brand={brand}
          />
        </Suspense>
        <DenSalesArsenalSection persona="recruiter" />
      </div>
    );
  }

  // Direct render path. Subject-mode Dens take a `brand` prop; self-mode
  // Dens render against the current user (no brand prop).
  return (
    <div className="space-y-3">
      <DenSwitchBar currentPersona={denType} />
      <Suspense fallback={<DenLoadingSkeleton />}>
        {denType === "athlete" && brand ? (
          <AthleteDen brand={brand} />
        ) : denType === "athlete" ? (
          <div className="p-6 text-sm text-muted-foreground">
            AthleteDen requires subject mode (open via /clients/:id).
          </div>
        ) : denType === "recruiter" ? (
          <RecruiterDen brand={brand ?? undefined} />
        ) : denType === "physician" ? (
          <PhysicianDen brand={brand ?? undefined} />
        ) : denType === "lawyer" ? (
          <LawyerDen brand={brand ?? undefined} />
        ) : denType === "investor" ? (
          <InvestorDen brand={brand ?? undefined} />
        ) : denType === "author" ? (
          <AuthorDen brand={brand ?? undefined} />
        ) : denType === "politician" ? (
          <PoliticianDen brand={brand ?? undefined} />
        ) : denType === "activist" ? (
          <ActivistDen brand={brand ?? undefined} />
        ) : denType === "religious" ? (
          <ReligiousDen brand={brand ?? undefined} />
        ) : denType === "real_estate_agent" ? (
          <RealEstateAgentDen brand={brand ?? undefined} />
        ) : denType === "sales_pro" ? (
          <SalesProDen brand={brand ?? undefined} />
        ) : denType === "coach" ? (
          <CoachDen brand={brand ?? undefined} />
        ) : denType === "speaker" ? (
          <SpeakerDen brand={brand ?? undefined} />
        ) : denType === "fractional_executive" ? (
          <FractionalDen brand={brand ?? undefined} />
        ) : denType === "financial_advisor" ? (
          <FinancialAdvisorDen brand={brand ?? undefined} />
        ) : denType === "consultant" ? (
          <ConsultantDen brand={brand ?? undefined} />
        ) : denType === "podcast_host" ? (
          <PodcastHostDen brand={brand ?? undefined} />
        ) : denType === "course_creator" ? (
          <CourseCreatorDen brand={brand ?? undefined} />
        ) : denType === "creative_freelancer" ? (
          <CreativeFreelancerDen brand={brand ?? undefined} />
        ) : denType === "real_estate_investor" ? (
          <RealEstateInvestorDen brand={brand ?? undefined} />
        ) : denType === "therapist" ? (
          <TherapistDen brand={brand ?? undefined} />
        ) : denType === "executive" ? (
          <ExecutiveDen brand={brand ?? undefined} />
        ) : denType === "journalist" ? (
          <JournalistDen brand={brand ?? undefined} />
        ) : denType === "academic" ? (
          <AcademicDen brand={brand ?? undefined} />
        ) : denType === "artist" ? (
          <ArtistDen brand={brand ?? undefined} />
        ) : denType === "military" ? (
          <MilitaryDen brand={brand ?? undefined} />
        ) : (
          <FounderDenFallback />
        )}
      </Suspense>
      {SALES_ARSENAL_PERSONAS.has(denType) && (
        <DenSalesArsenalSection persona={denType} />
      )}
    </div>
  );
}

export default DenDispatcher;
